import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import axios from '@axios';
import { RootState } from '@redux/reducers';
import { OrderType } from '@models/order/order-type';
import { PlatformRequest } from '@models/order/platform/request';
import { TypeResponse, TypeResponseApi } from '@models/order/type/response';
import { PlatformResponse } from '@models/order/platform/response';
import { toQueryParameters } from '@extensions/object';
import { addNotification } from '../notification';
import { translate } from '@components/i18n';
import { ThunkCallback } from '../model/thunk-callback';
import { EntityString } from '@models/entity';

export const fetchAvailableTypes = createAsyncThunk<TypeResponse[]>(
    'select-type/fetchAvailableTypes',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<TypeResponseApi[]>(
                '/tipopedido/all',
            );

            const result = response.data.map((u) =>
                Object.assign(new TypeResponseApi(), u),
            );

            return result.map((u) => u.toTypeResponse());
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchAvailablePlatforms = createAsyncThunk<
    PlatformResponse[],
    PlatformRequest
>('select-type/fetchAvailablePlatforms', async (req, thunkAPI) => {
    try {
        thunkAPI.dispatch(updateTypeRequest(req));

        const state = thunkAPI.getState() as RootState;

        const params = toQueryParameters({
            companhiaCodigo: req.companhiaCodigo
                ? req.companhiaCodigo
                : state.selectCustomer.customer.company,
        });

        const response = await axios.get<TypeResponseApi[]>(
            '/filial/byusuario?' + params,
        );

        const { status, data } = response;

        if (status === 200) {
            const result = response.data.map((u) =>
                Object.assign(new TypeResponseApi(), u),
            );

            return result.map((u) => u.toTypeResponse());
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

export const fetchAllAvailablePlatforms = createAsyncThunk<PlatformResponse[]>(
    'select-type/fetchAllAvailablePlatforms',
    async (_, thunkAPI) => {
        try {
            const response = await axios.get<TypeResponseApi[]>('/filial/all');

            const { status, data } = response;

            if (status === 200) {
                const result = response.data.map((u) =>
                    Object.assign(new TypeResponseApi(), u),
                );

                return result.map((u) => u.toTypeResponse());
            } else {
                return thunkAPI.rejectWithValue(data);
            }
        } catch (e) {
            const { response } = e;
            const { data } = response;
            const { error } = data;
            const { message } = error;

            return thunkAPI.rejectWithValue(message);
        }
    },
);

type TChangeBranch = {
    codigoFilial: number
    pedidoId: number
}

export const requestChangeBranch = createAsyncThunk<void, TChangeBranch>(
    'select-type/change-branch', async (req, thunkAPI) => {
        try {
            const response = await axios.put<any>(`/pedido/atualizar/${req.pedidoId}/${req.codigoFilial}`);

            const { status, data } = response;

            if (status !== 200) {
                return thunkAPI.rejectWithValue(data);
              }
        } catch (e) {
            let message = e?.response?.data?.error?.message

            if(e?.response?.data?.length > 0){
                message = e.response.data[0]?.msgUsuario
            }

            thunkAPI.dispatch(
                addNotification({
                    type: 'error',
                    message: message,
                    title: translate('general.errorT'),
                }),
            );

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const requestVerifyChangeBranch = createAsyncThunk<void, ThunkCallback<TChangeBranch>>(
    'select-type/change-branch', async (req, thunkAPI) => {
        try {
            const response = await axios.get<any>(`/pedido/validar/${req.data.pedidoId}/${req.data.codigoFilial}`);

            const { status, data } = response;

            if (status !== 200) {
                req.onError()
                return thunkAPI.rejectWithValue(data);
            }
            req.onSuccess()
        } catch (e) {
            let message = e?.response?.data?.error?.message

            if(e?.response?.data?.length > 0){
                message = e.response.data[0]?.msgUsuario
            }

            req.onError(message)

            return thunkAPI.rejectWithValue(message);
        }
    },
);

export const fetchAvailablePlatformsByDivision = createAsyncThunk<
    PlatformResponse[],
    string
>('select-type/fetchAvailablePlatformsByDivision', async (req, thunkAPI) => {
    try {
        const response = await axios.get<TypeResponseApi[]>(
            '/filial/bydivisao?divisao=' + req.replace('undefined', ''),
        );

        const { status, data } = response;

        if (status === 200) {
            const result = response.data.map((u) =>
                Object.assign(new TypeResponseApi(), u),
            );

            return result.map((u) => u.toTypeResponse());
        } else {
            return thunkAPI.rejectWithValue(data);
        }
    } catch (e) {
        const { response } = e;
        const { data } = response;
        const { error } = data;
        const { message } = error;

        return thunkAPI.rejectWithValue(message);
    }
});

interface IOrderTypeSelectorState {
    orderType?: {platformCopy: EntityString} & OrderType;
    availableTypes: TypeResponse[];
    availablePlatforms: PlatformResponse[];
    allAvailablePlatforms: PlatformResponse[];
    availablePlatformsByDivision: PlatformResponse[];
    request: PlatformRequest;
    isFetching: boolean;
    isSuccess: boolean;
    isError: boolean;
    errorMessage: string;
    selectedPlatform?: PlatformResponse

    isFetchingPlatform: boolean;
    isSuccessPlatform: boolean;
    isErrorPlatform: boolean;
    errorMessagePlatform: string;
    isVisibleModalBranch: boolean
}

const initialState: IOrderTypeSelectorState = {
    allAvailablePlatforms: [],
    availableTypes: [],
    availablePlatforms: [],
    availablePlatformsByDivision: [],
    request: {},
    isFetching: true,
    isSuccess: false,
    isError: false,
    isFetchingPlatform: true,
    isSuccessPlatform: false,
    isErrorPlatform: false,
    errorMessage: '',
    errorMessagePlatform: '',
    isVisibleModalBranch: false,
};

const selectTypeSlice = createSlice({
    name: 'selectTypeSlice',
    initialState,
    reducers: {
        selectType: (state, action: PayloadAction<PlatformRequest>) => {
            const date = action.payload.date;
            const platform = state.availablePlatforms.find(
                (i) => i.id === action.payload.platform,
            );
            const type = state.availableTypes.find(
                (i) => i.id === action.payload.type,
            );

            state.orderType = { date, platform, type, platformCopy: platform };

            return state;
        },
        selectTypePlatform: (state, action: PayloadAction<PlatformRequest>) => {
            const platform = state.availablePlatforms.find(
                (i) => i.id === action.payload.platform,
            );

            state.orderType = { ...state.orderType, platform };

            return state;
        },
        setOrderType: (state, action: PayloadAction<OrderType>) => {
            state.orderType = { ...action.payload, platformCopy: action.payload.platform }
            return state;
        },
        updateTypeRequest: (state, action: PayloadAction<PlatformRequest>) => {
            state.request = {
                ...action.payload,
                date: action.payload.date,
            };

            return state;
        },
        clearState: (state) => {
            state = { ...initialState };
            return state;
        },
        addAvailablePlatform: (state, action: PayloadAction<PlatformResponse>) => {
            state.availablePlatforms = [action.payload];
            state.isFetchingPlatform = false
            return state;
        },
        updatePlatform: (state, action: PayloadAction<PlatformResponse>) => {
            state.selectedPlatform = action.payload
            return state;
        },
        showModalBranch: (state) => {
            state.isVisibleModalBranch = true
            return state;
        },
        closeModalBranch: (state) => {
            state.isVisibleModalBranch = false
            return state;
        }
    },
    extraReducers: {
        [fetchAvailableTypes.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<TypeResponse[]>,
        ) => {
            state.availableTypes = payload;
            state.isFetching = false;
            state.isSuccess = true;
            return state;
        },
        [fetchAvailableTypes.rejected.toString()]: (state, action) => {
            state.isFetching = false;
            state.isError = true;
            state.errorMessage = action.payload;
        },
        [fetchAvailableTypes.pending.toString()]: (state) => {
            state.isFetching = true;
            state.isError = false;
        },
        [fetchAllAvailablePlatforms.pending.toString()]: (state) => {
            state.isFetchingPlatform = true;
            state.isErrorPlatform = false;
            return state;
        },
        [fetchAllAvailablePlatforms.rejected.toString()]: (state, action) => {
            state.isFetchingPlatform = false;
            state.isErrorPlatform = true;
            state.errorMessagePlatform = action.payload;
            return state;
        },
        [fetchAllAvailablePlatforms.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<TypeResponse[]>,
        ) => {
            state.isFetchingPlatform = false;
            state.isSuccessPlatform = true;
            state.allAvailablePlatforms = payload;
            return state;
        },
        [fetchAvailablePlatforms.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PlatformResponse[]>,
        ) => {
            state.availablePlatforms = payload;
            state.isFetchingPlatform = false;
            state.isSuccessPlatform = true;
            return state;
        },
        [fetchAvailablePlatforms.rejected.toString()]: (state, action) => {
            state.isFetchingPlatform = false;
            state.isErrorPlatform = true;
            state.errorMessagePlatform = action.payload;
        },
        [fetchAvailablePlatforms.pending.toString()]: (state) => {
            state.isFetchingPlatform = true;
            state.isErrorPlatform = false;
        },
        [fetchAvailablePlatformsByDivision.fulfilled.toString()]: (
            state,
            { payload }: PayloadAction<PlatformResponse[]>,
        ) => {
            state.availablePlatformsByDivision = payload;
            return state;
        },
    },
});

export const orderTypeSelector = (state: RootState): IOrderTypeSelectorState =>
    state.selectType;

export const { selectType, updateTypeRequest, clearState, addAvailablePlatform, updatePlatform, selectTypePlatform, showModalBranch, closeModalBranch, setOrderType
 } =
    selectTypeSlice.actions;

export default selectTypeSlice.reducer;
